import { HTMLAttributes } from 'react'
import Link from 'next/link'

import Button from '@/components/base/button'
import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'
import TextEffect from '@/components/base/text-effect'
import Typography from '@/components/base/typography'

import { textBlockRevealAnimation } from './animations'
import { cn } from '@/lib/utils'
import { WPLink } from '@/types'

export type TextBlockProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    title: string
    paragraph_1: string
    paragraph_2: string
    double_paragraph: boolean
    alignment: 'left' | 'right'
    link: WPLink
  }>

const TextBlock = ({
  title,
  paragraph_1,
  paragraph_2,
  double_paragraph,
  alignment = 'left',
  link,
  ...props
}: TextBlockProps) => {
  return (
    <Motion animation={textBlockRevealAnimation}>
      {(motionRef) => (
        <Section ref={motionRef} {...props}>
          <Container>
            <Grid>
              <div
                className={cn(
                  'col-span-2 md:col-span-4 lg:col-span-6',
                  {
                    'md:col-span-6 lg:col-span-10 xl:col-span-8':
                      double_paragraph
                  },
                  {
                    'lg:col-start-2 xl:col-start-2': alignment === 'left',
                    'md:col-start-3 lg:col-start-5':
                      alignment === 'right' && !double_paragraph,
                    'lg:col-start-3 xl:col-start-5':
                      alignment === 'right' && double_paragraph
                  },
                  'grid gap-y-spacing-lg',
                  {
                    'lg:gap-y-spacing-2xl': double_paragraph
                  }
                )}
              >
                <div className="grid grid-cols-2 gap-x-gutter">
                  {paragraph_1 ? (
                    <div
                      className={cn('col-span-2 grid', {
                        'md:col-span-1': double_paragraph
                      })}
                    >
                      <Typography variant="h4" component="h2" data-motion-title>
                        {title}
                      </Typography>
                    </div>
                  ) : null}
                </div>

                <div className="grid grid-cols-2 gap-x-gutter gap-y-spacing-lg">
                  {paragraph_1 ? (
                    <div
                      className={cn('col-span-2', {
                        'md:col-span-1': double_paragraph
                      })}
                    >
                      <Typography
                        variant="large"
                        paragraph
                        data-motion-paragraph1
                      >
                        {paragraph_1}
                      </Typography>
                    </div>
                  ) : null}

                  {paragraph_2 ? (
                    <div
                      className={cn('col-span-2', {
                        'md:col-span-1': double_paragraph
                      })}
                    >
                      <Typography
                        variant="regular"
                        paragraph
                        data-motion-paragraph2
                      >
                        {paragraph_2}
                      </Typography>
                    </div>
                  ) : null}
                </div>

                {link?.url ? (
                  <div>
                    <Button
                      className="min-h-[4.8rem]"
                      isLiquid={true}
                      isInverted={false}
                      asChild
                      variant="primary"
                      data-motion-cta
                    >
                      <Link
                        href={link.url}
                        target={link.target || '_self'}
                        scroll={false}
                      >
                        <TextEffect>
                          <Typography variant="regular">
                            {link.title}
                          </Typography>
                        </TextEffect>
                      </Link>
                    </Button>
                  </div>
                ) : null}
              </div>
            </Grid>
          </Container>
        </Section>
      )}
    </Motion>
  )
}

export default TextBlock
